import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchPlaceSuggestions } from "../utils/placesAutocomplete";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const SLOT_ROLES  = ["Your Business", "Competitor 1", "Competitor 2"];
const SLOT_COLORS = ["#1E293B", "#64748B", "#94A3B8"];

const STOP_WORDS = new Set([
  "the","a","an","and","or","but","in","on","at","to","for","of","with",
  "is","was","are","were","be","been","has","have","had","do","did","not",
  "it","its","this","that","they","their","very","so","my","we","i","he",
  "she","you","our","all","from","by","as","up","out","about","over","than",
  "me","us","him","her","who","what","when","where","how","just","too","also",
  "would","could","should","will","really","even","much","more","most","well",
  "got","get","no","yes","if","am","every","any","some","there","here","food",
  "place","good","great","nice","best","bad","service","staff","time","order",
  "like","went","come","came","back","make","made","said","take","took","know",
  "written","review","write","written","rating","rated","star","stars","visit",
  "definitely","recommend","highly","experience","overall","always","never",
  "again","first","last","still","only","also","around","away","without",
  // contractions with apostrophe stripped
  "didnt","wasnt","isnt","dont","wont","cant","shouldnt","wouldnt","couldnt",
  "thats","ive","youre","theyre","weve","hadnt","hasnt","wouldve","couldve",
  "shouldve","lets","heres","whats","theres","whos","wasnt","doesnt","didnt",
  // generic quality / filler adjectives
  "truly","special","amazing","awesome","excellent","perfect","wonderful",
  "terrible","horrible","awful","worst","poor","absolute","quite","rather",
  "pretty","fairly","slightly","extremely","incredibly","surprisingly",
  // generic location / business type words
  "hotel","hotels","room","rooms","stay","stayed","staying","property",
  "bangalore","bengaluru","mumbai","delhi","india","city","road","street",
  "resort","resorts","restaurant","restaurants","pool","lobby","check",
  // misc
  "actually","basically","literally","certainly","absolutely","completely",
  "everything","nothing","something","anything","anyone","everyone","someone",
  "thing","things","way","ways","day","days","night","nights","trip","visit",
]);

const PLACEHOLDER = "no written review";

function buildFreqMap(reviews) {
  const freq = {};
  reviews
    .filter(r => r.comment.toLowerCase().trim() !== PLACEHOLDER)
    .forEach(r =>
      r.comment.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).forEach(w => {
        if (w.length > 3 && !STOP_WORDS.has(w)) freq[w] = (freq[w] || 0) + 1;
      })
    );
  return freq;
}

function getKeywordsBySentiment(reviews, minRating, maxRating, n = 5, extraStop = null) {
  const all      = reviews || [];
  const target   = all.filter(r => r.rating >= minRating && r.rating <= maxRating);
  const opposite = all.filter(r => minRating >= 4 ? r.rating <= 2 : r.rating >= 4);
  const targetFreq   = buildFreqMap(target);
  const oppositeFreq = buildFreqMap(opposite);
  const targetCount   = Math.max(target.length, 1);
  const oppositeCount = Math.max(opposite.length, 1);
  return Object.entries(targetFreq)
    .filter(([w, cnt]) => {
      if (extraStop && extraStop.has(w)) return false;
      const tr = cnt / targetCount;
      const or = (oppositeFreq[w] || 0) / oppositeCount;
      return or === 0 || tr >= or * 2;
    })
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([w]) => w);
}

const EMPTY_SLOT = { name: "", address: "", data_id: "", reviews: null, loading: false, error: "", sentiment: null };

// ─── SVG metric icons ─────────────────────────────────────────────
const Icons = {
  star: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  fiveStar: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  thumbUp: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/>
      <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
    </svg>
  ),
  thumbDown: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3z"/>
      <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
    </svg>
  ),
  reply: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/>
    </svg>
  ),
  count: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
      <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  ),
  shield: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  neutral: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <circle cx="9" cy="9" r="1" fill="currentColor" stroke="none"/>
      <circle cx="15" cy="9" r="1" fill="currentColor" stroke="none"/>
    </svg>
  ),
};

// ─── Slot Card ──────────────────────────────────────────────────────
function SlotCard({ slot, index, role, onSelectCandidate, onClear }) {
  const [input,      setInput]      = useState("");
  const [candidates, setCandidates] = useState([]);
  const [showDrop,   setShowDrop]   = useState(false);
  const [activeIdx,  setActiveIdx]  = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef     = useRef(null);
  const isOwn       = index === 0;
  const color       = SLOT_COLORS[index];

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchCandidates = useCallback(async q => {
    const list = (await fetchPlaceSuggestions(q)).map(s => ({
      data_id: s.description || s.main_text,
      name:    s.main_text || s.description,
      address: s.secondary_text || "",
      rating:  null,
    }));
    setCandidates(list);
    setShowDrop(list.length > 0);
    setActiveIdx(-1);
  }, []);

  const handleChange = e => {
    setInput(e.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchCandidates(e.target.value), 150);
  };

  const handleSelect = c => {
    setInput("");
    setCandidates([]);
    setShowDrop(false);
    onSelectCandidate(index, c);
  };

  const handleKeyDown = e => {
    if (!showDrop || !candidates.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, candidates.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); handleSelect(candidates[activeIdx]); }
    else if (e.key === "Escape") setShowDrop(false);
  };

  if (slot.loading) {
    return (
      <div className="cv-slot cv-slot--loading" style={{ "--slot-color": color }}>
        <div className="cv-slot-accent-bar" />
        <div className="cv-slot-eq">
          {[1,2,3,4,5].map(n => <div key={n} className="cv-slot-eq-bar" />)}
        </div>
        <span>Loading {slot.name}…</span>
      </div>
    );
  }

  if (slot.name && slot.reviews) {
    return (
      <div className="cv-slot cv-slot--filled" style={{ "--slot-color": color }}>
        <div className="cv-slot-accent-bar" />
        <div className="cv-slot-info">
          <div className="cv-slot-role-label">{role}</div>
          <span className="cv-slot-name" title={slot.name}>{slot.name.split(",")[0]}</span>
          {slot.address && <span className="cv-slot-addr">{slot.address.split(",")[0]}</span>}
        </div>
        <div className="cv-slot-right">
          <button className="cv-slot-clear" onClick={() => onClear(index)} title="Remove">✕</button>
        </div>
      </div>
    );
  }

  if (isOwn) {
    return (
      <div className="cv-slot cv-slot--locked" style={{ "--slot-color": color }}>
        <div className="cv-slot-accent-bar" />
        <div className="cv-slot-lock-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <div className="cv-slot-locked-content">
          <span className="cv-slot-role-label">{role}</span>
          <span className="cv-slot-locked-msg">Connect Google Business in Settings to auto-load your business.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-slot cv-slot--empty" ref={wrapRef} style={{ "--slot-color": color }}>
      <div className="cv-slot-accent-bar" />
      <div className="cv-slot-empty-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </div>
      <div className="cv-slot-input-wrap">
        <div className="cv-slot-role-label">{role}</div>
        <input
          className="cv-slot-input"
          placeholder={`Search ${role.toLowerCase()}…`}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (input.trim().length >= 2) fetchCandidates(input); }}
          autoComplete="off"
        />
        {showDrop && candidates.length > 0 && (
          <ul className="cv-ac-list">
            {candidates.map((c, i) => (
              <li
                key={c.data_id}
                className={`cv-ac-item${i === activeIdx ? " active" : ""}`}
                onMouseDown={() => handleSelect(c)}
              >
                <span className="cv-ac-name">{c.name}</span>
                <span className="cv-ac-addr">{c.address}</span>
                {c.rating && <span className="cv-ac-rating">★ {c.rating}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {slot.error && <span className="cv-slot-err">Not found</span>}
    </div>
  );
}

// ─── Thumb SVGs ──────────────────────────────────────────────────────
const ThumbUpSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3z"/>
    <rect x="2" y="9" width="4" height="12" rx="1"/>
  </svg>
);
const ThumbDownSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3z"/>
    <rect x="18" y="3" width="4" height="12" rx="1"/>
  </svg>
);

// ─── Welcome empty state (vector illustration) ─────────────────────
function WelcomeEmpty() {
  return (
    <div className="cv-welcome">
      <div className="cv-welcome-hero">
        <h2 className="cv-welcome-title">Compare Businesses Side by Side</h2>
        <p className="cv-welcome-sub">Add a competitor above to unlock a full intelligence report — 4 key metrics, topic comparison, growth opportunities, sentiment breakdown and AI insights.</p>
      </div>

      <div className="cv-welcome-visual">
        <svg viewBox="0 0 560 230" fill="none" xmlns="http://www.w3.org/2000/svg" className="cv-welcome-svg" aria-hidden="true">
          <style>{`
            .wv-card        { fill:#ffffff; stroke:#E0E0E0; stroke-width:1.5; }
            .wv-elev        { fill:#F5F5F5; }
            .wv-text        { fill:#1A1A1A; }
            .wv-muted       { fill:#888888; }
            .wv-dimbar      { fill:#E0E0E0; }
            .wv-lock-circle { fill:#F1F5F9; stroke:#CBD5E1; }
            .wv-lock-stroke { stroke:#64748B; }
            .wv-lock-fill   { fill:#64748B; }
            .wv-lock-label  { fill:#64748B; }
            .wv-lock-sub    { fill:#94A3B8; }
            .wv-overlay     { fill:#ffffff; }
            @media(prefers-color-scheme:dark){
              .wv-card        { fill:#1a1a1a; stroke:#2A2A2A; }
              .wv-elev        { fill:#252525; }
              .wv-text        { fill:#F0F0F0; }
              .wv-muted       { fill:#666666; }
              .wv-dimbar      { fill:#2A2A2A; }
              .wv-lock-circle { fill:#1e293b; stroke:#334155; }
              .wv-lock-stroke { stroke:#94A3B8; }
              .wv-lock-fill   { fill:#94A3B8; }
              .wv-lock-label  { fill:#94A3B8; }
              .wv-lock-sub    { fill:#64748B; }
              .wv-overlay     { fill:#1a1a1a; }
            }
          `}</style>

          {/* ── Left card (Your Business) ── */}
          <rect x="24" y="28" width="198" height="178" rx="14" className="wv-card"/>
          <rect x="24" y="28" width="198" height="44" rx="14" className="wv-elev"/>
          <rect x="24" y="58" width="198" height="14" className="wv-elev"/>
          {/* icon placeholder */}
          <rect x="38" y="36" width="22" height="22" rx="6" className="wv-text" opacity="0.1"/>
          <text x="49" y="51" textAnchor="middle" fontSize="11">🏪</text>
          {/* business name lines */}
          <rect x="68" y="39" width="96" height="7" rx="3.5" className="wv-text" opacity="0.15"/>
          <rect x="68" y="51" width="60" height="5" rx="2.5" className="wv-muted" opacity="0.35"/>
          {/* big score */}
          <text x="38" y="100" fontFamily="Lexend,sans-serif" fontSize="30" fontWeight="900" className="wv-text" opacity="0.88">4.8</text>
          <text x="86" y="100" fontFamily="Lexend,sans-serif" fontSize="11" className="wv-muted" opacity="0.6">/ 5 stars</text>
          {/* star row */}
          {[0,1,2,3,4].map(i => (
            <text key={i} x={38 + i * 17} y="116" fontSize="12" fill={i < 4 ? "#f59e0b" : "#D1D5DB"}>★</text>
          ))}
          <text x="38" y="131" fontFamily="Lexend,sans-serif" fontSize="8" className="wv-muted" opacity="0.65">132 reviews</text>
          {/* metric bars */}
          {[
            { label:"Positive", pct:0.84, color:"#10b981", y:148 },
            { label:"5-Star",   pct:0.66, color:"#3b82f6", y:164 },
            { label:"Reply",    pct:0.51, color:"#8b5cf6", y:180 },
            { label:"Crisis",   pct:0.72, color:"#f59e0b", y:196 },
          ].map(({ label, pct, color, y }) => (
            <g key={label}>
              <text x="38" y={y} fontFamily="Lexend,sans-serif" fontSize="7.5" className="wv-muted" opacity="0.75">{label}</text>
              <rect x="80" y={y - 9} width="118" height="6" rx="3" className="wv-dimbar"/>
              <rect x="80" y={y - 9} width={118 * pct} height="6" rx="3" fill={color} opacity="0.75"/>
              <text x="202" y={y} fontFamily="Lexend,sans-serif" fontSize="7.5" className="wv-muted" opacity="0.75">{Math.round(pct * 100)}%</text>
            </g>
          ))}
          {/* winner pill */}
          <rect x="76" y="11" width="68" height="17" rx="8.5" fill="#1E293B"/>
          <text x="110" y="23" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7.5" fontWeight="800" fill="#ffffff" letterSpacing="0.8">LEADING</text>

          {/* ── VS center badge ── */}
          <circle cx="280" cy="117" r="32" fill="#1E293B" opacity="0.93"/>
          <circle cx="280" cy="117" r="27" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.18"/>
          <text x="280" y="123" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="14" fontWeight="900" fill="#ffffff" letterSpacing="2.5">VS</text>
          {/* dashed connectors */}
          <line x1="224" y1="117" x2="246" y2="117" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="3 3"/>
          <line x1="314" y1="117" x2="336" y2="117" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="3 3"/>

          {/* ── Right card (Competitor — locked) ── */}
          {/* blur filter def */}
          <defs>
            <filter id="wv-blur"><feGaussianBlur stdDeviation="2.8"/></filter>
            <clipPath id="wv-clip-right"><rect x="338" y="28" width="198" height="178" rx="14"/></clipPath>
          </defs>

          {/* card shell */}
          <rect x="338" y="28" width="198" height="178" rx="14" className="wv-card" strokeDasharray="6 4"/>
          {/* header bar */}
          <rect x="338" y="28" width="198" height="44" rx="14" className="wv-elev"/>
          <rect x="338" y="58" width="198" height="14" className="wv-elev"/>

          {/* blurred mirrored content (same layout as left, grayed out) */}
          <g filter="url(#wv-blur)" clipPath="url(#wv-clip-right)" opacity="0.45">
            {/* icon */}
            <rect x="352" y="36" width="22" height="22" rx="6" fill="#CBD5E1"/>
            {/* name lines */}
            <rect x="382" y="39" width="88" height="7" rx="3.5" fill="#CBD5E1"/>
            <rect x="382" y="51" width="54" height="5" rx="2.5" fill="#CBD5E1"/>
            {/* big score */}
            <text x="352" y="100" fontFamily="Lexend,sans-serif" fontSize="30" fontWeight="900" fill="#94A3B8">4.2</text>
            <text x="400" y="100" fontFamily="Lexend,sans-serif" fontSize="11" fill="#94A3B8">/ 5 stars</text>
            {/* stars */}
            {[0,1,2,3,4].map(i => (
              <text key={i} x={352 + i * 17} y="116" fontSize="12" fill={i < 3 ? "#f59e0b" : "#D1D5DB"}>★</text>
            ))}
            <text x="352" y="131" fontFamily="Lexend,sans-serif" fontSize="8" fill="#94A3B8">98 reviews</text>
            {/* metric bars */}
            {[
              { pct:0.62, color:"#94A3B8", y:148 },
              { pct:0.45, color:"#94A3B8", y:164 },
              { pct:0.38, color:"#94A3B8", y:180 },
              { pct:0.55, color:"#94A3B8", y:196 },
            ].map(({ pct, color, y }, idx) => (
              <g key={idx}>
                <rect x="352" y={y - 9} width="118" height="6" rx="3" fill="#E2E8F0"/>
                <rect x="352" y={y - 9} width={118 * pct} height="6" rx="3" fill={color}/>
              </g>
            ))}
          </g>

          {/* frosted overlay */}
          <rect x="338" y="28" width="198" height="178" rx="14" className="wv-overlay" opacity="0.55" clipPath="url(#wv-clip-right)"/>

          {/* lock icon + label — center of right card */}
          <circle cx="437" cy="112" r="30" className="wv-lock-circle" strokeWidth="1.5"/>
          {/* lock body */}
          <rect x="425" y="108" width="24" height="18" rx="4" fill="none" className="wv-lock-stroke" strokeWidth="2"/>
          {/* lock shackle */}
          <path d="M430 108 V104 a7 7 0 0 1 14 0 V108" fill="none" className="wv-lock-stroke" strokeWidth="2" strokeLinecap="round"/>
          {/* lock keyhole */}
          <circle cx="437" cy="117" r="2.5" className="wv-lock-fill"/>
          <rect x="436" y="119" width="2" height="4" rx="1" className="wv-lock-fill"/>

          <text x="437" y="158" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="8.5" fontWeight="700" className="wv-lock-label">Search to unlock</text>
          <text x="437" y="170" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="7.5" className="wv-lock-sub">Add competitor above</text>

          {/* floating +12% bubble */}
          <rect x="36" y="10" width="36" height="15" rx="7.5" fill="#10b981" opacity="0.88"/>
          <text x="54" y="21" textAnchor="middle" fontFamily="Lexend,sans-serif" fontSize="8" fontWeight="700" fill="#ffffff">+12%</text>
        </svg>
      </div>

      <div className="cv-welcome-tags">
        {["4 key metrics","Topic keyword comparison","Win / loss tracker","AI competitive insights","Sentiment analysis","Crisis response rate"].map(t => (
          <span key={t} className="cv-welcome-tag">{t}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Compute slot stats ──────────────────────────────────────────────
function computeStats(slot) {
  const n   = slot.reviews.length;
  const avg = slot.reviews.reduce((s, r) => s + r.rating, 0) / n;
  const negReviews = slot.reviews.filter(r => r.rating <= 2);
  return {
    avg,
    fivePct:    Math.round(slot.reviews.filter(r => r.rating === 5).length / n * 100),
    posPct:     Math.round(slot.reviews.filter(r => r.rating >= 4).length / n * 100),
    neuPct:     Math.round(slot.reviews.filter(r => r.rating === 3).length / n * 100),
    negPct:     Math.round(slot.reviews.filter(r => r.rating <= 2).length / n * 100),
    replyPct:   Math.round(slot.reviews.filter(r => r.has_reply).length / n * 100),
    crisisPct:  negReviews.length > 0
                  ? Math.round(negReviews.filter(r => r.has_reply).length / negReviews.length * 100)
                  : 0,
    total:      n,
    shortName:  slot.name.split(",")[0],
    initials:   slot.name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase()).join(""),
  };
}

// ─── Shared metrics list ─────────────────────────────────────────────
function buildMetrics(ms) {
  return [
    { label: "Review Volume",   desc: "Total reviews collected",         icon: Icons.count,     vals: ms.map(m => m.total),     fmt: v => v.toLocaleString(), max: null, higher: true  },
    { label: "Positive Rate",   desc: "Reviews rated 4 or 5 stars",      icon: Icons.thumbUp,   vals: ms.map(m => m.posPct),    fmt: v => `${v}%`,            max: 100,  higher: true  },
    { label: "Negative Rate",   desc: "Reviews rated 1 or 2 stars",      icon: Icons.thumbDown, vals: ms.map(m => m.negPct),    fmt: v => `${v}%`,            max: 100,  higher: false },
    { label: "Crisis Response", desc: "% of 1-2★ reviews replied to",    icon: Icons.shield,    vals: ms.map(m => m.crisisPct), fmt: v => `${v}%`,            max: 100,  higher: true  },
  ];
}

function computeWins(metrics, ms) {
  return ms.map((_, i) =>
    metrics.reduce((sum, row) => {
      const allSame = row.vals.every(v => v === row.vals[0]);
      if (allSame) return sum;
      const best = row.higher ? Math.max(...row.vals) : Math.min(...row.vals);
      return sum + (row.vals[i] === best ? 1 : 0);
    }, 0)
  );
}

// ─── Trophy SVG (animated, winner only) ─────────────────────────────
function TrophySvg() {
  return (
    <svg className="cv-vs-trophy-icon" viewBox="0 0 48 52" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Winner">
      {/* Cup body */}
      <path d="M10 4h28v20a14 14 0 0 1-28 0V4z" fill="#fbbf24"/>
      {/* Rim highlight */}
      <rect x="10" y="4" width="28" height="5" rx="1" fill="#fde68a"/>
      {/* Left handle */}
      <path d="M10 10H5a5 5 0 0 0 0 10h5" fill="#f59e0b"/>
      {/* Right handle */}
      <path d="M38 10h5a5 5 0 0 1 0 10h-5" fill="#f59e0b"/>
      {/* Inner sheen */}
      <ellipse cx="20" cy="11" rx="5" ry="3" fill="#fef3c7" opacity="0.55"/>
      {/* Star on cup */}
      <path d="M24 15l2.2 6.7h7l-5.7 4.1 2.2 6.8L24 28.3l-5.7 4.3 2.2-6.8-5.7-4.1h7z" fill="#fff" opacity="0.88"/>
      {/* Stem */}
      <rect x="22" y="38" width="4" height="6" rx="1" fill="#d97706"/>
      {/* Base */}
      <rect x="15" y="44" width="18" height="5" rx="2.5" fill="#b45309"/>
      {/* Base top shine */}
      <rect x="16" y="44" width="16" height="2" rx="1" fill="#d97706" opacity="0.45"/>
    </svg>
  );
}

// ─── Versus Table (2 businesses — Versus.com style) ──────────────────
function VersusTable({ slots, ms, metrics, winCounts, winnerIdx }) {
  return (
    <div className="cv-table-wrap cv-versus-wrap">
      {/* Single unified grid — header + all rows share the same columns */}
      <div className="cv-vs-grid">

        {/* ── Header row — 3 explicit cells keep the grid order correct ── */}

        {/* Left business — [spacer | centered text | trophy] */}
        <div className="cv-vs-biz-hd">
          <div className="cv-vs-hd-spacer" />
          <div className="cv-vs-hd-content">
            <span className="cv-vs-biz-name">{slots[0].name.split(",")[0]}</span>
            <span className="cv-vs-biz-role">{slots[0].origIdx === 0 ? "Your Business" : `Competitor ${slots[0].origIdx}`}</span>
            <div className="cv-vs-wins-pill">
              <span className="cv-vs-wins-num">{winCounts[0]}</span>
              <span className="cv-vs-wins-of">/{metrics.length} wins</span>
            </div>
          </div>
          <div className="cv-vs-trophy-slot">
            {winnerIdx === 0 && <TrophySvg />}
          </div>
        </div>

        {/* Center VS */}
        <div className="cv-vs-hdr-mid">
          <span className="cv-vs-header-vs">VS</span>
        </div>

        {/* Right business — [spacer | centered text | trophy] */}
        <div className="cv-vs-biz-hd">
          <div className="cv-vs-hd-spacer" />
          <div className="cv-vs-hd-content">
            <span className="cv-vs-biz-name">{slots[1].name.split(",")[0]}</span>
            <span className="cv-vs-biz-role">{slots[1].origIdx === 0 ? "Your Business" : `Competitor ${slots[1].origIdx}`}</span>
            <div className="cv-vs-wins-pill">
              <span className="cv-vs-wins-num">{winCounts[1]}</span>
              <span className="cv-vs-wins-of">/{metrics.length} wins</span>
            </div>
          </div>
          <div className="cv-vs-trophy-slot">
            {winnerIdx === 1 && <TrophySvg />}
          </div>
        </div>


        {/* ── Metric rows ── */}
        {metrics.map(row => {
          const [v0, v1] = row.vals;
          const allSame  = v0 === v1;
          const best     = row.higher ? Math.max(v0, v1) : Math.min(v0, v1);
          const worst    = row.higher ? Math.min(v0, v1) : Math.max(v0, v1);
          const absMax   = row.max ?? Math.max(v0, v1, 1);
          const pct0     = Math.min((v0 / absMax) * 100, 100);
          const pct1     = Math.min((v1 / absMax) * 100, 100);
          const win0  = !allSame && v0 === best;
          const win1  = !allSame && v1 === best;
          const lose0 = !allSame && v0 === worst;
          const lose1 = !allSame && v1 === worst;
          const barColor0 = win0 ? "#10b981" : lose0 ? "#ef4444" : "var(--cv-bar-neutral)";
          const barColor1 = win1 ? "#10b981" : lose1 ? "#ef4444" : "var(--cv-bar-neutral)";

          return (
            <div key={row.label} className="cv-vs-row-wrap">
              {/* Left value cell */}
              <div className={`cv-vs-cell cv-vs-cell--left${win0?" cv-vs-cell--win":""}${lose0?" cv-vs-cell--lose":""}`}>
                <span className={`cv-vs-val${win0?" cv-vs-val--win":""}${lose0?" cv-vs-val--lose":""}`}>{row.fmt(v0)}</span>
                <div className="cv-vs-track cv-vs-track--left">
                  <div className="cv-vs-fill" style={{ width:`${Math.max(pct0,2)}%`, background:barColor0 }}/>
                </div>
                {(win0||lose0) && (
                  <span className={`cv-vs-badge${win0?" cv-vs-badge--win":" cv-vs-badge--lose"}`}>
                    {win0 ? <ThumbUpSvg /> : <ThumbDownSvg />}
                  </span>
                )}
              </div>

              {/* Center metric label */}
              <div className="cv-vs-metric">
                <span className="cv-vs-metric-icon">{row.icon}</span>
                <span className="cv-vs-metric-name">{row.label}</span>
                <span className="cv-vs-metric-desc">{row.desc}</span>
              </div>

              {/* Right value cell — bar on left, value on right */}
              <div className={`cv-vs-cell cv-vs-cell--right${win1?" cv-vs-cell--win":""}${lose1?" cv-vs-cell--lose":""}`}>
                {(win1||lose1) && (
                  <span className={`cv-vs-badge${win1?" cv-vs-badge--win":" cv-vs-badge--lose"}`}>
                    {win1 ? <ThumbUpSvg /> : <ThumbDownSvg />}
                  </span>
                )}
                <div className="cv-vs-track cv-vs-track--right">
                  <div className="cv-vs-fill" style={{ width:`${Math.max(pct1,2)}%`, background:barColor1 }}/>
                </div>
                <span className={`cv-vs-val${win1?" cv-vs-val--win":""}${lose1?" cv-vs-val--lose":""}`}>{row.fmt(v1)}</span>
              </div>
            </div>
          );
        })}

      </div>
    </div>
  );
}

// ─── Column Table (3 businesses — G2-style columns) ──────────────────
function ColumnTable({ slots, ms, metrics, winCounts, winnerIdx }) {
  return (
    <div className="cv-table-wrap">
      <div className="cv-table-inner" style={{ "--col-count": slots.length, "--winner-col-idx": winnerIdx >= 0 ? winnerIdx : -1 }}>

        <div className="cv-th-row">
          <div className="cv-th cv-th--label">Metric</div>
          {slots.map((slot, i) => {
            const isWinner = i === winnerIdx;
            return (
              <div key={i} className={`cv-th cv-th--biz${isWinner ? " cv-th--winner" : ""}`} style={{ "--sc": SLOT_COLORS[slot.origIdx] }}>
                <span className="cv-th-name">{slot.name.split(",")[0]}</span>
                <span className="cv-th-role">{slot.origIdx === 0 ? "Your Business" : `Competitor ${slot.origIdx}`}</span>
                <div className="cv-th-wins">
                  <span className="cv-th-wins-num">{winCounts[i]}</span>
                  <span className="cv-th-wins-of">/ {metrics.length} wins</span>
                </div>
              </div>
            );
          })}
        </div>

        {metrics.map(row => {
          const allSame = row.vals.every(v => v === row.vals[0]);
          const best    = row.higher ? Math.max(...row.vals) : Math.min(...row.vals);
          const worst   = row.higher ? Math.min(...row.vals) : Math.max(...row.vals);
          const absMax  = row.max ?? Math.max(...row.vals, 1);
          return (
            <div key={row.label} className="cv-tr">
              <div className="cv-td cv-td--label">
                <span className="cv-td-icon">{row.icon}</span>
                <span className="cv-td-metric">{row.label}</span>
                <span className="cv-td-desc">{row.desc}</span>
              </div>
              {row.vals.map((val, i) => {
                const isBest      = !allSame && val === best;
                const isWorst     = !allSame && slots.length >= 2 && val === worst;
                const isWinnerCol = i === winnerIdx;
                const barPct      = Math.min((val / absMax) * 100, 100);
                const barColor    = isBest ? "#10b981" : isWorst ? "#ef4444" : "var(--cv-bar-neutral)";
                return (
                  <div key={i} className={`cv-td cv-td--data${isBest ? " cv-td--best" : ""}${isWorst ? " cv-td--worst" : ""}${isWinnerCol ? " cv-td--winner-col" : ""}`}>
                    <div className="cv-td-top">
                      <span className="cv-td-val">{row.fmt(val)}</span>
                      {isBest  && <span className="cv-td-thumb cv-td-thumb--up"><ThumbUpSvg /></span>}
                      {isWorst && <span className="cv-td-thumb cv-td-thumb--down"><ThumbDownSvg /></span>}
                    </div>
                    <div className="cv-td-bar-track">
                      <div className="cv-td-bar-fill" style={{ width: `${Math.max(barPct, 2)}%`, background: barColor }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Comparison Table (delegates to Versus or Column) ───────────
function ComparisonTable({ slots }) {
  const ms      = useMemo(() => slots.map(computeStats), [slots]);
  const metrics = useMemo(() => buildMetrics(ms), [ms]);
  const winCounts  = useMemo(() => computeWins(metrics, ms), [metrics, ms]);
  const maxWins    = Math.max(...winCounts);
  const winnerIdx  = maxWins > 0 ? winCounts.indexOf(maxWins) : -1;

  if (slots.length === 2) {
    return <VersusTable slots={slots} ms={ms} metrics={metrics} winCounts={winCounts} winnerIdx={winnerIdx} />;
  }
  return <ColumnTable slots={slots} ms={ms} metrics={metrics} winCounts={winCounts} winnerIdx={winnerIdx} />;
}

// ─── Competitive Edge ─────────────────────────────────────────────────
function CompetitiveEdge({ slots }) {
  const mySlot = slots.find(s => s.origIdx === 0);
  if (!mySlot || slots.length < 2) return null;

  const myStats   = computeStats(mySlot);
  const compSlots = slots.filter(s => s.origIdx !== 0);

  const metrics = [
    { key: "posPct",    label: "Positive Rate",   higher: true,  fmt: v => `${v}%` },
    { key: "negPct",    label: "Negative Rate",   higher: false, fmt: v => `${v}%` },
    { key: "crisisPct", label: "Crisis Response", higher: true,  fmt: v => `${v}%` },
    { key: "total",     label: "Review Volume",   higher: true,  fmt: v => v.toLocaleString() },
  ];

  const ahead   = [];
  const improve = [];

  metrics.forEach(({ key, label, higher, fmt }) => {
    const myVal = myStats[key];
    compSlots.forEach(cs => {
      const cStats  = computeStats(cs);
      const cVal    = cStats[key];
      const diff    = higher ? myVal - cVal : cVal - myVal;
      const compName = cs.name.split(",")[0];
      if (diff > 0) ahead.push({ label, myVal: fmt(myVal), compVal: fmt(cVal), compName, diff });
      else if (diff < 0) improve.push({ label, myVal: fmt(myVal), compVal: fmt(cVal), compName, diff });
    });
  });

  ahead.sort((a, b) => b.diff - a.diff);
  improve.sort((a, b) => a.diff - b.diff);

  return (
    <div className="cv-edge">
      <div className="cv-edge-col cv-edge-col--ahead">
        <div className="cv-edge-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
          </svg>
          <span>Where you lead</span>
        </div>
        {ahead.length === 0
          ? <p className="cv-edge-empty">No clear lead yet — add a competitor to compare.</p>
          : ahead.slice(0, 5).map((item, i) => (
              <div key={i} className="cv-edge-item cv-edge-item--good">
                <span className="cv-edge-metric">{item.label}</span>
                <span className="cv-edge-vals">{item.myVal} vs {item.compVal}</span>
              </div>
            ))
        }
      </div>
      <div className="cv-edge-col cv-edge-col--improve">
        <div className="cv-edge-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>
          </svg>
          <span>Where to improve</span>
        </div>
        {improve.length === 0
          ? <p className="cv-edge-empty">You lead on all compared metrics.</p>
          : improve.slice(0, 5).map((item, i) => (
              <div key={i} className="cv-edge-item cv-edge-item--warn">
                <span className="cv-edge-metric">{item.label}</span>
                <span className="cv-edge-vals">{item.myVal} vs {item.compVal} ({item.compName})</span>
              </div>
            ))
        }
      </div>
    </div>
  );
}

// ─── Sentiment Cards ──────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function SentimentSection({ slots }) {
  return (
    <div className="cv-sent-section">
      <div className="cv-sent-title">What Customers Say</div>
      <div className="cv-sent-grid" style={{ "--col-count": slots.length }}>
        {slots.map(slot => {
          const origIdx = slot.origIdx;
          return (
            <div key={slot.data_id || slot.name} className="cv-sent-card" style={{ "--sc": SLOT_COLORS[origIdx] }}>
              <div className="cv-sent-card-header">
                <span className="cv-sent-biz-name">{slot.name.split(",")[0]}</span>
                <span className="cv-sent-role">{origIdx === 0 ? "Your Business" : `Competitor ${origIdx}`}</span>
              </div>
              <div className="cv-sent-label cv-sent-label--love">What they love</div>
              {slot.sentiment === null
                ? <p className="cv-sentiment-loading">Analysing…</p>
                : slot.sentiment.love?.length > 0
                  ? <ul className="cv-sentiment-list cv-sentiment-pos">
                      {slot.sentiment.love.map((pt, j) => <li key={j}>{pt}</li>)}
                    </ul>
                  : <p className="cv-sentiment-loading">Not enough positive feedback yet.</p>
              }
              <div className="cv-sent-label cv-sent-label--concern">Common concerns</div>
              {slot.sentiment === null
                ? <p className="cv-sentiment-loading">Analysing…</p>
                : slot.sentiment.concern?.length > 0
                  ? <ul className="cv-sentiment-list cv-sentiment-neg">
                      {slot.sentiment.concern.map((pt, j) => <li key={j}>{pt}</li>)}
                    </ul>
                  : <p className="cv-sentiment-loading">No major complaints found.</p>
              }
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── AI Insights — markdown renderer ─────────────────────────────────
function renderInsight(text) {
  const lines = text.split("\n").filter(l => l.trim());
  return lines.map((raw, i) => {
    const l = raw.trim();

    // Section header: **Section N: Title** or ## Title
    if (l.match(/^\*\*Section\s+\d+/i) || l.match(/^#{1,3}\s/)) {
      const clean = l.replace(/\*\*/g, "").replace(/^#{1,3}\s/, "");
      return <div key={i} className="cv-ai-section-hd">{clean}</div>;
    }

    // Numbered item: 1. **Title**: body
    const numMatch = l.match(/^(\d+)\.\s+\*\*(.+?)\*\*[:.]\s*(.*)/);
    if (numMatch) {
      const bodyHtml = numMatch[3].replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return (
        <div key={i} className="cv-ai-item">
          <span className="cv-ai-item-num">{numMatch[1]}</span>
          <div className="cv-ai-item-body">
            <strong className="cv-ai-item-title">{numMatch[2]}</strong>
            {numMatch[3] && <p className="cv-ai-item-text" dangerouslySetInnerHTML={{ __html: bodyHtml }} />}
          </div>
        </div>
      );
    }

    // Inline bold in plain line
    const boldHtml = l.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    if (boldHtml !== l) {
      return <p key={i} className="cv-ai-para" dangerouslySetInnerHTML={{ __html: boldHtml }} />;
    }

    return <p key={i} className="cv-ai-para">{l}</p>;
  });
}

// ─── AI Insights Panel ────────────────────────────────────────────────
function AiInsightsPanel({ slots, token }) {
  const [state, setState] = useState({ loading: false, text: "", error: "" });

  const generate = () => {
    setState({ loading: true, text: "", error: "" });
    const businesses = slots.map(s => ({
      name:         s.name,
      avg:          (s.reviews.reduce((sum, r) => sum + r.rating, 0) / s.reviews.length).toFixed(1),
      pos_pct:      Math.round(s.reviews.filter(r => r.rating >= 4).length / s.reviews.length * 100),
      neg_pct:      Math.round(s.reviews.filter(r => r.rating <= 2).length / s.reviews.length * 100),
      reply_pct:    Math.round(s.reviews.filter(r => r.has_reply).length / s.reviews.length * 100),
      pos_keywords: getKeywordsBySentiment(s.reviews, 4, 5, 5),
      neg_keywords: getKeywordsBySentiment(s.reviews, 1, 2, 5),
    }));
    fetch(`${API}/compare/insights`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ businesses }),
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setState({ loading: false, text: d.insights, error: "" }))
      .catch(() => setState({ loading: false, text: "", error: "Could not generate insights. Try again." }));
  };

  return (
    <div className="cv-ai-panel">
      <div className="cv-ai-header">
        <div className="cv-ai-title-wrap">
          <span className="cv-ai-pill">AI</span>
          <span className="cv-ai-title">Competitive Insights</span>
        </div>
        {!state.text && (
          <button className="cv-ai-btn" onClick={generate} disabled={state.loading}>
            {state.loading
              ? <><span className="cv-ai-spinner"/>{" Analysing…"}</>
              : "Generate Insights"
            }
          </button>
        )}
      </div>

      {state.error && <p className="cv-ai-error">{state.error}</p>}

      {state.text && (
        <div className="cv-ai-body">{renderInsight(state.text)}</div>
      )}

      {!state.text && !state.loading && !state.error && (
        <p className="cv-ai-hint">
          Get AI-powered competitive insights — personalised tips based on how customers rate your competitors.
        </p>
      )}
    </div>
  );
}

// ─── Topics / Keyword Comparison ──────────────────────────────────────
function wordsToSentence(words, isPos) {
  if (!words.length) return null;
  const w = words.slice(0, 5);
  if (w.length === 1) {
    return isPos
      ? `Customers love the ${w[0]} here.`
      : `Main complaint is about ${w[0]}.`;
  }
  const last = w[w.length - 1];
  const rest = w.slice(0, -1).join(", ");
  return isPos
    ? `Customers frequently praise the ${rest} and ${last}.`
    : `Customers often raise concerns about ${rest} and ${last}.`;
}

function TopicsComparison({ slots }) {
  const slotTopics = useMemo(() =>
    slots.map(slot => {
      // Filter out the business's own name words so they don't pollute results
      const nameStop = new Set(
        slot.name.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).filter(w => w.length > 2)
      );
      return {
        name: slot.name.split(",")[0],
        pos:  getKeywordsBySentiment(slot.reviews, 4, 5, 5, nameStop),
        neg:  getKeywordsBySentiment(slot.reviews, 1, 2, 5, nameStop),
      };
    }),
    [slots]
  );

  const TagIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  );

  if (slots.length === 2) {
    return (
      <div className="cv-topics-wrap">
        <div className="cv-topics-hd">
          <TagIcon />
          <span className="cv-topics-title">What Customers Talk About</span>
        </div>

        {/* Loved topics */}
        <div className="cv-topics-section">
          <div className="cv-topics-section-bar cv-topics-section-bar--pos">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#10b981">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span>What they love</span>
          </div>
          <div className="cv-topics-two-cols">
            {slotTopics.map((st, i) => {
              const sentence = wordsToSentence(st.pos, true);
              return (
                <div key={i} className="cv-topics-sent-block">
                  <div className="cv-topics-sent-biz">{st.name}</div>
                  <p className="cv-topics-sent cv-topics-sent--pos">
                    {sentence || "Not enough review data yet."}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Complaints */}
        <div className="cv-topics-section">
          <div className="cv-topics-section-bar cv-topics-section-bar--neg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>Common complaints</span>
          </div>
          <div className="cv-topics-two-cols">
            {slotTopics.map((st, i) => {
              const sentence = wordsToSentence(st.neg, false);
              return (
                <div key={i} className="cv-topics-sent-block">
                  <div className="cv-topics-sent-biz">{st.name}</div>
                  <p className="cv-topics-sent cv-topics-sent--neg">
                    {sentence || "No major complaints found."}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // 3-business grid
  return (
    <div className="cv-topics-wrap">
      <div className="cv-topics-hd">
        <TagIcon />
        <span className="cv-topics-title">What Customers Talk About</span>
      </div>
      <div className="cv-topics-grid" style={{ "--col-count": slots.length }}>
        {slotTopics.map((st, i) => (
          <div key={i} className="cv-topics-grid-col">
            <div className="cv-topics-grid-biz">{st.name}</div>
            <div className="cv-topics-grid-lbl cv-topics-grid-lbl--pos">What they love</div>
            <p className="cv-topics-sent cv-topics-sent--pos">{wordsToSentence(st.pos, true) || "Not enough data."}</p>
            <div className="cv-topics-grid-lbl cv-topics-grid-lbl--neg">Complaints</div>
            <p className="cv-topics-sent cv-topics-sent--neg">{wordsToSentence(st.neg, false) || "No major complaints."}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Growth Opportunities (improvement suggestions) ───────────────────
function ImprovementSuggestions({ slots }) {
  const mySlot = slots.find(s => s.origIdx === 0);
  if (!mySlot) return null;
  const compSlots = slots.filter(s => s.origIdx !== 0);
  if (!compSlots.length) return null;

  const myStats = computeStats(mySlot);
  const rival = compSlots.reduce((best, cs) => {
    return computeStats(cs).posPct > computeStats(best).posPct ? cs : best;
  }, compSlots[0]);
  const rivalStats = computeStats(rival);
  const rivalName  = rival.name.split(",")[0];

  const gaps = [];

  if (rivalStats.posPct > myStats.posPct + 3) {
    const gap   = rivalStats.posPct - myStats.posPct;
    const extra = Math.round(myStats.total * gap / 100);
    gaps.push({
      metric: "Positive Rate",
      arrow:  `${myStats.posPct}% → ${rivalStats.posPct}%`,
      text:   `Improving your positive reviews by ${gap}% would generate ~${extra} more satisfied-customer reviews, putting you ahead of ${rivalName} in perceived quality and Google visibility.`,
      urgent: gap > 10,
    });
  }

  if (myStats.negPct > rivalStats.negPct + 3) {
    const gap      = myStats.negPct - rivalStats.negPct;
    const reducible = Math.round(myStats.total * gap / 100);
    gaps.push({
      metric: "Negative Rate",
      arrow:  `${myStats.negPct}% → ${rivalStats.negPct}%`,
      text:   `Reducing negative reviews by ${gap}% (~${reducible} reviews) would close your biggest gap with ${rivalName} — every negative review removed improves your first impression on Google.`,
      urgent: gap > 5,
    });
  }

  if (rivalStats.crisisPct > myStats.crisisPct + 5) {
    const negCount = Math.round(myStats.total * myStats.negPct / 100);
    const missed   = Math.round(negCount * (100 - myStats.crisisPct) / 100);
    gaps.push({
      metric: "Crisis Response",
      arrow:  `${myStats.crisisPct}% → ${rivalStats.crisisPct}%`,
      text:   `${missed} of your negative review${missed !== 1 ? "s are" : " is"} unanswered. Responding to each one — like ${rivalName} does — can turn unhappy customers around and show future visitors that you care.`,
      urgent: missed > 3,
    });
  }

  if (rivalStats.total > myStats.total + 15) {
    const gap = rivalStats.total - myStats.total;
    gaps.push({
      metric: "Review Volume",
      arrow:  `${myStats.total} → ${rivalStats.total}`,
      text:   `${rivalName} has ${gap} more reviews than you — more reviews mean more trust and higher Google ranking. Simply asking every satisfied customer to leave a review can close this gap fast.`,
      urgent: gap > 50,
    });
  }

  if (!gaps.length) {
    return (
      <div className="cv-suggest-wrap">
        <div className="cv-suggest-hd">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 1 18"/><polyline points="16 7 22 7 22 13"/>
          </svg>
          <span className="cv-suggest-title">Growth Opportunities</span>
        </div>
        <p className="cv-suggest-great">You are already matching or beating {rivalName} on all key metrics. Keep up the excellent work!</p>
      </div>
    );
  }

  return (
    <div className="cv-suggest-wrap">
      <div className="cv-suggest-hd">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 1 18"/><polyline points="16 7 22 7 22 13"/>
        </svg>
        <div className="cv-suggest-title-block">
          <span className="cv-suggest-title">Growth Opportunities</span>
          <span className="cv-suggest-sub">Improve these areas to outperform {rivalName} and win more customers</span>
        </div>
      </div>
      <div className="cv-suggest-list">
        {gaps.map((g, i) => (
          <div key={i} className={`cv-suggest-card${g.urgent ? " cv-suggest-card--urgent" : ""}`}>
            <div className="cv-suggest-card-top">
              <span className="cv-suggest-metric">{g.metric}</span>
              <span className="cv-suggest-arrow">{g.arrow}</span>
            </div>
            <p className="cv-suggest-text">{g.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main CompareView ─────────────────────────────────────────────────
function CompareView({ onExit }) {
  const { token } = useAuth();
  const [slots, setSlots] = useState([
    { ...EMPTY_SLOT },
    { ...EMPTY_SLOT },
    { ...EMPTY_SLOT },
  ]);

  const updateSlot = (i, updates) =>
    setSlots(prev => { const n = [...prev]; n[i] = { ...n[i], ...updates }; return n; });

  const fetchSentiment = (i, reviews) => {
    fetch(`${API}/business/sentiment-summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ reviews }),
    })
      .then(r => r.json())
      .then(data => updateSlot(i, { sentiment: data }))
      .catch(() => updateSlot(i, { sentiment: {} }));
  };

  const loadSlotReviews = (i, name, data_id, address) => {
    const searchQuery = data_id || name;
    updateSlot(i, { name, address, data_id, loading: true, error: "", reviews: null, sentiment: null });
    fetch(`${API}/reviews/${encodeURIComponent(searchQuery)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        const reviews = data.reviews || [];
        updateSlot(i, { reviews, loading: false });
        fetchSentiment(i, reviews);
      })
      .catch(() => updateSlot(i, { ...EMPTY_SLOT, error: "failed" }));
  };

  useEffect(() => {
    fetch(`${API}/my-business`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (data.connected && data.reviews && data.reviews.length > 0) {
          setSlots(prev => {
            const next = [...prev];
            next[0] = { name: data.name, address: data.address || "", data_id: "__my_business__",
                        reviews: data.reviews, loading: false, error: "", sentiment: null };
            return next;
          });
          fetchSentiment(0, data.reviews);
        }
      })
      .catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCompetitor = (i, name, data_id, address) => {
    fetch(`${API}/competitors`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slot: i, name, data_id, address }),
    }).catch(() => {});
  };

  const selectCandidate = (i, candidate) => {
    loadSlotReviews(i, candidate.name, candidate.data_id, candidate.address);
    if (i === 1 || i === 2) saveCompetitor(i, candidate.name, candidate.data_id, candidate.address);
  };

  const clearSlot = i => {
    updateSlot(i, { ...EMPTY_SLOT });
    if (i === 1 || i === 2) {
      fetch(`${API}/competitors/${i}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
  };

  const filledSlots = useMemo(
    () => slots.map((s, origIdx) => ({ ...s, origIdx })).filter(s => s.reviews !== null && s.reviews.length > 0),
    [slots]
  );
  const filledCount = filledSlots.length;

  return (
    <div className="cv-container">
      <div className="cv-header">
        <div className="cv-header-left">
          <span className="cv-title">Business Comparison</span>
          {filledCount > 0 && <span className="cv-filled-hint">{filledCount} of 3 loaded</span>}
        </div>
        <button className="cv-exit-btn" onClick={onExit}>Exit Compare</button>
      </div>

      <div className="cv-slots-row">
        {slots.map((slot, i) => (
          <SlotCard
            key={i}
            slot={slot}
            index={i}
            role={SLOT_ROLES[i]}
            onSelectCandidate={selectCandidate}
            onClear={clearSlot}
          />
        ))}
      </div>

      {filledCount === 0 && <WelcomeEmpty />}

      {filledCount === 1 && (
        <div className="cv-one-hint">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          Add a competitor above to start the comparison.
        </div>
      )}

      {filledCount >= 2 && <ComparisonTable slots={filledSlots} />}
      {filledCount >= 2 && <TopicsComparison slots={filledSlots} />}
      {filledCount >= 2 && <ImprovementSuggestions slots={filledSlots} />}
      {filledCount >= 2 && <CompetitiveEdge slots={filledSlots} />}
      {filledCount >= 2 && <AiInsightsPanel slots={filledSlots} token={token} />}
    </div>
  );
}

export default CompareView;
